// src/pages/Colostro/colostro.js

/**
 * Colostro: o que se avalia e os registros de DEMONSTRAÇÃO da página.
 *
 * A qualidade do colostro tem três eixos, e só um é microbiológico:
 *  - imunológico (IgG): o produtor mede com refratômetro Brix na hora da coleta;
 *  - microbiológico: contagem bacteriana total (e coliformes) — o ensaio;
 *  - manejo: tempo entre parto, coleta e fornecimento, volume e armazenamento.
 *
 * Referências usuais (Godden 2008; McGuirk & Collins 2004; Embrapa):
 *  - Brix ≥ 22% ≈ IgG ≥ 50 g/L (bom); abaixo de 18% insuficiente;
 *  - CBT < 100.000 UFC/mL; coliformes < 10.000 UFC/mL;
 *  - fornecer ~10% do peso vivo (~4 L) o quanto antes, no máximo até 6 h após o parto;
 *  - refrigerar ou congelar até 1 h após a coleta; descongelar em banho-maria ≤ 45–55 °C.
 *
 * Hoje NÃO há análise de colostro (só o protocolo existe: nenhum modelo, nenhum dado de
 * manejo no backend). Os registros abaixo existem para apresentar o fluxo ao produtor e
 * a tela os marca como exemplo. Quando a análise existir, eles saem e entra a API.
 */

export const REFERENCIAS_COLOSTRO = Object.freeze({
    brixBom: 22,
    brixMinimo: 18,
    cbtMaxima: 100_000,
    horasMaximasFornecimento: 6,
});

/** Leitura do Brix: rótulo e tom (success/warning/error). */
export const avaliarBrix = (brix) => {
    if (!Number.isFinite(brix)) return null;
    if (brix >= REFERENCIAS_COLOSTRO.brixBom) return { rotulo: 'Alta qualidade', tom: 'success' };
    if (brix >= REFERENCIAS_COLOSTRO.brixMinimo) return { rotulo: 'Qualidade intermediária', tom: 'warning' };
    return { rotulo: 'Qualidade insuficiente', tom: 'error' };
};

/** CBT do colostro contra a referência de 100.000 UFC/mL. */
export const avaliarCbt = (ufcMl) => {
    if (!Number.isFinite(ufcMl)) return null;
    return ufcMl < REFERENCIAS_COLOSTRO.cbtMaxima
        ? { rotulo: 'Dentro da referência', tom: 'success' }
        : { rotulo: 'Acima da referência', tom: 'error' };
};

/** Tempo entre o parto e o fornecimento, em minutos; null sem as duas datas. */
export const minutosAteFornecimento = (registro) => {
    const parto = new Date(registro?.parto);
    const fornecimento = new Date(registro?.fornecimento);
    if (Number.isNaN(parto.getTime()) || Number.isNaN(fornecimento.getTime())) return null;
    return Math.round((fornecimento - parto) / 60_000);
};

/** Registros de exemplo (os mesmos do protótipo). Datas em horário de Brasília. */
export const REGISTROS_DEMONSTRACAO = Object.freeze([
    {
        id: 'demo-col-001',
        codigo: 'COL-001',
        vaca: 'Vaca 248',
        bezerra: 'Bezerra B-026',
        brix: 24.8,
        cbtUfcMl: 38_000,
        armazenamento: 'Refrigerado',
        recipiente: 'Frasco COL-001',
        numeroSerie: '7C9EBD4A11F0',
        volumeL: 3.5,
        parto: '2026-10-01T05:40:00-03:00',
        coleta: '2026-10-01T06:20:00-03:00',
        fornecimento: '2026-10-01T06:45:00-03:00',
        resultado: '2026-10-01T14:35:00-03:00',
    },
    {
        id: 'demo-col-002',
        codigo: 'COL-002',
        vaca: 'Vaca 173',
        bezerra: 'Bezerra B-025',
        brix: 22.5,
        cbtUfcMl: 62_000,
        armazenamento: 'Congelado',
        recipiente: 'Frasco COL-002',
        numeroSerie: '7C9EBD4A1204',
        volumeL: 3,
        parto: '2026-09-30T05:40:00-03:00',
        coleta: '2026-09-30T06:20:00-03:00',
        fornecimento: '2026-09-30T06:45:00-03:00',
        resultado: '2026-09-30T14:35:00-03:00',
    },
    {
        id: 'demo-col-003',
        codigo: 'COL-003',
        vaca: 'Vaca 312',
        bezerra: 'Bezerra B-024',
        brix: 25.1,
        cbtUfcMl: 29_000,
        armazenamento: 'Congelado',
        recipiente: 'Frasco COL-003',
        numeroSerie: '7C9EBD4A0F88',
        volumeL: 3.8,
        parto: '2026-09-29T05:40:00-03:00',
        coleta: '2026-09-29T06:20:00-03:00',
        fornecimento: '2026-09-29T06:45:00-03:00',
        resultado: '2026-09-29T14:35:00-03:00',
    },
]);
